import type { FC } from "react";
import type { SearchModalSectionData } from "./searchModal.js";
import { SearchModalLoadingRow } from "./searchModalLoadingRow.js";
import { SearchModalLoadingSectionView } from "./searchModalLoadingSectionView.js";

export interface SearchModalLoadingStateProps {
  /**
   * The sections the results will be laid out in — the same list the loaded
   * modal renders, so the skeleton can't stand for a list that never arrives.
   * Each section's `placeholder` says how many rows to hold, and in what shape.
   */
  sections: readonly SearchModalSectionData[];
  /**
   * Stands in for the all-results action, which leads the loaded list. Without
   * it the skeleton is a row short of what replaces it, and everything below
   * shifts down as the results land.
   */
  hasAllResultsAction?: boolean;
}

export const SearchModalLoadingState: FC<SearchModalLoadingStateProps> = (
  props,
) => {
  const { sections, hasAllResultsAction = false } = props;

  return (
    // Laid out like the listbox it stands in for: rows flush, titled groups
    // bringing their own separation.
    <div
      className="flex shrink-0 flex-col gap-px"
      role="status"
      aria-label="Loading results"
    >
      {hasAllResultsAction ? (
        <SearchModalLoadingRow
          index={0}
          showBadge={false}
          showTrailing={false}
        />
      ) : null}
      {sections.map((section) => (
        <SearchModalLoadingSectionView
          key={section.id}
          title={section.title ?? ""}
          rowCount={section.placeholder?.rowCount}
          showBadge={section.placeholder?.hasBadge}
          showTrailing={section.placeholder?.hasTrailing}
        />
      ))}
      <span className="sr-only">Loading search results...</span>
    </div>
  );
};
